import type { ReactNode } from 'react'
import { LanguageToggle } from './LanguageToggle.js'

/**
 * Full-bleed workshop photo behind the sign-in and forced-password-change
 * screens — the only two pages rendered before AuthGate lets anyone into the
 * app proper, so this is the first thing staff see. A light blur and a dark
 * wash keep the photo readable as texture without competing with the form,
 * regardless of whether the app's own theme is light or dark: the card here
 * is deliberately NOT theme-aware, since "always dark glass over a photo" is
 * a fixed design choice for this one screen, not a mode the rest of the app
 * switches with it.
 */
export function AuthBackground({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 overflow-y-auto">
      {/* The photo itself, behind everything. */}
      <div
        className="fixed inset-0 bg-cover bg-center animate-[fade-in_900ms_ease]"
        style={{ backgroundImage: 'url(/login-background.jpg)' }}
        aria-hidden
      />
      {/* A light blur plus a dark wash over it — enough to read the shape of
          the workshop without it fighting the form for attention. */}
      <div className="fixed inset-0 bg-black/45 backdrop-blur-[2px] animate-[fade-in_900ms_ease]" aria-hidden />

      <div className="absolute top-4 end-4 z-10 p-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
        {/* Forced to light text: this page ignores the app's own light/dark
            theme, so the toggle's usual theme-linked color could otherwise
            render dark-on-dark in light mode. */}
        <LanguageToggle className="!text-white hover:!bg-white/10" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-full p-4 py-12">
        {children}
      </div>
    </div>
  )
}
